import { FormEvent, useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import { DevConsoleOrderRow, OrderStatusCode, OrderTraceKey } from '@/console/api/types'
import { PanelState } from '@/console/state/panelState'
import { clearOrderSearchForm, loadOrderSearchForm, saveOrderSearchForm } from '@/console/state/searchFormStorage'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { DataTable, DataTableColumn, DataTableRow } from '@/console/ui/DataTable'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { ResultState } from '@/console/ui/ResultState'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Tag } from '@/console/ui/Tag'
import {
  DEFAULT_ORDER_SEARCH_LIMIT,
  EMPTY_ORDER_SEARCH_FORM,
  ORDER_SEARCH_LIMITS,
  ORDER_STATUS_GROUPS,
  OrderSearchForm,
  orderStatusMeta,
  orderTypeLabel,
  togglePage,
  toggleId,
  toSearchParams,
  validateOrderSearch,
} from './orderSearch'
import { useOrderSearch } from '@/console/data/devConsoleOrders'
import { MAX_REDISPATCH_IDS } from './redispatch'
import styles from './OrderSearchPanel.module.css'

export type OrderSearchPanelProps = {
  onRedispatch: (ids: number[]) => void
  redispatchLocked: boolean
  /**
   * PR 코멘트(LOGISPRD-13034, H2) — 가장 최근 재발사가 성공해서 응답에 담겨 온 standardOrderId 목록
   * (`useManualRedispatch.lastSubmittedIds`). 값이 바뀔 때마다 그 id 들만 선택에서 뺀다 — 화면에서
   * 사라진 옛 행이 아니라 방금 보낸 id 만. 실패한 제출은 이 값이 바뀌지 않으므로 선택이 그대로 남는다.
   */
  redispatchedIds?: number[] | null
  onTrace?: (key: OrderTraceKey) => void
}

/**
 * LOGISPRD-13034 — F-OPT `주문 조회`(`s-fopt-search`). 확정 시안 v1.
 *
 * 체크한 행은 `onRedispatch` 로 올리고, 확인 다이얼로그 · 제출 · 결과는 아래 `RedispatchPanel` 이
 * 그대로 맡는다(`useManualRedispatch`). 조회를 다시 하면 선택을 비운다 — 화면에서 사라진 옛 행이
 * 선택에 남아 재발사되는 것을 막는다. 전체 건수는 세지 않는다(컷 하나에 수만 건 · 인계서).
 */
export const OrderSearchPanel = ({ onRedispatch, redispatchLocked, redispatchedIds, onTrace }: OrderSearchPanelProps): JSX.Element => {
  // 저장값은 마운트 때 한 번만 읽는다 — 두 useState 가 각각 부르면 같은 값을 두 번 읽고 두 번 파싱한다.
  const [restored] = useState(loadOrderSearchForm)
  const [form, setForm] = useState<OrderSearchForm>(() => restored?.form ?? EMPTY_ORDER_SEARCH_FORM)
  const [limit, setLimit] = useState<number>(() => restored?.limit ?? DEFAULT_ORDER_SEARCH_LIMIT)
  const [selected, setSelected] = useState<number[]>([])
  const { state, search, loadMore } = useOrderSearch()

  // PR 코멘트(LOGISPRD-13034, H2) — redispatchedIds 가 바뀔 때만(=제출이 성공할 때만) 그 id 들을
  // 선택에서 뺀다. 실패는 lastSubmittedIds 를 건드리지 않으므로 이 효과가 돌지 않는다.
  useEffect(() => {
    if (redispatchedIds && redispatchedIds.length > 0) {
      setSelected((prev) => prev.filter((id) => !redispatchedIds.includes(id)))
    }
  }, [redispatchedIds])

  const rule = validateOrderSearch(form)
  const loading = state.status === 'loading'
  const items = state.status === 'ready' ? state.items : []
  // items 와 같이 메모한다 — 매 렌더 새 배열이면 아래 columns 의 useMemo 가 항상 빗나간다(리뷰 지적).
  const pageIds = useMemo(() => items.map((r) => r.standardOrderId), [items])
  const overLimit = selected.length > MAX_REDISPATCH_IDS

  // 입력 즉시 저장한다 — 새로고침이 언제 일어날지 모른다(조회 버튼을 눌렀을 때만 저장하면 늦다).
  const update = <K extends keyof OrderSearchForm>(key: K, value: OrderSearchForm[K]): void => {
    const next = { ...form, [key]: value }
    setForm(next)
    saveOrderSearchForm({ form: next, limit })
  }

  const changeLimit = (value: number): void => {
    setLimit(value)
    saveOrderSearchForm({ form, limit: value })
  }

  const reset = (): void => {
    setForm(EMPTY_ORDER_SEARCH_FORM)
    // 건수도 함께 되돌린다 — 저장값만 지우고 화면의 건수를 두면, 초기화 직후 새로고침했을 때
    // 화면에 보이던 건수가 조용히 기본값으로 바뀐다.
    setLimit(DEFAULT_ORDER_SEARCH_LIMIT)
    clearOrderSearchForm()
  }

  const toggleStatus = (code: OrderStatusCode): void =>
    update('orderStatuses', form.orderStatuses.includes(code) ? form.orderStatuses.filter((c) => c !== code) : [...form.orderStatuses, code])

  const submit = (event?: FormEvent): void => {
    event?.preventDefault()
    if (!rule.ok || loading) return
    setSelected([])
    search(toSearchParams(form, limit))
  }

  const panelState: PanelState<DevConsoleOrderRow[]> =
    state.status === 'ready'
      ? // 첫 페이지가 비어도 다음 커서가 있으면(state.hasNext) empty 로 접지 않는다 — "더 보기" 블록이
        // ResultState 의 children 안에 있어, empty 로 접히면 다음 페이지로 갈 방법이 사라진다.
        state.items.length === 0 && !state.hasNext
        ? { status: 'empty', reason: '조건에 맞는 주문이 없어요.' }
        : { status: 'ready', data: state.items }
      : state.status === 'idle'
        ? { status: 'empty', reason: '조건을 넣고 조회를 누르면 최신 주문부터 가져와요.' }
        : state

  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.includes(id))

  // PR 코멘트(LOGISPRD-13034, H4) — `update` 가 글자마다 `setForm` 하는데, 그 렌더에서 `columns`·
  // `rows` 가 매번 통째로 재생성됐다. "더 보기" 로 누적된 행(예: 1,000행 × 9칸)까지 매 keystroke 마다
  // 다시 만드는 비용이라 `useMemo` 로 감싼다 — 실제로 쓰는 값만 의존성에 넣는다. 체크박스 onChange 는
  // 이미 `setSelected((prev) => ...)` 함수형 업데이트라 `form`·`selected` 클로저와 무관하고
  // `setSelected` 자체는 안정 참조라 의존성에 넣지 않는다.
  const columns: DataTableColumn[] = useMemo(
    () => [
      {
        key: 'pick',
        label: (
          <input
            type="checkbox"
            aria-label="보이는 행 전체 선택"
            checked={allOnPage}
            onChange={(e) => setSelected((prev) => togglePage(prev, pageIds, e.target.checked))}
          />
        ),
      },
      { key: 'id', label: 'standard_order.id' },
      { key: 'order', label: '주문번호 · 옵션번호' },
      { key: 'status', label: '상태' },
      { key: 'type', label: '유형' },
      { key: 'cut', label: '컷 id', align: 'right' },
      { key: 'collected', label: '수집일', align: 'right' },
      { key: 'goods', label: '상품 · SKU' },
      { key: 'trace', label: '' },
    ],
    [allOnPage, pageIds],
  )

  const rows: DataTableRow[] = useMemo(
    () =>
      items.map((r) => {
        const status = orderStatusMeta(r.orderStatus)
        const picked = selected.includes(r.standardOrderId)
        // trim 기준으로 판단한다 — 공백/빈 문자열이면 Trace 탭의 validateTraceKey 가 invalid 로 막으므로
        // 두 곳이 같은 조건을 봐야 버튼이 켜지고도 못 들어가는 상황이 안 생긴다.
        const traceKey = r.refNo1?.trim() && r.orderOptionNumber?.trim() ? { refNo1: r.refNo1, orderOptionNumber: r.orderOptionNumber } : null
        return {
          key: String(r.standardOrderId),
          selected: picked,
          cells: {
            pick: (
              <input
                type="checkbox"
                aria-label={`${r.standardOrderId} 선택`}
                checked={picked}
                onChange={(e) => setSelected((prev) => toggleId(prev, r.standardOrderId, e.target.checked))}
              />
            ),
            id: (
              <span className={styles.idCell}>
                {r.standardOrderId}
                <Button
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard?.writeText(String(r.standardOrderId)).catch(() => undefined)
                  }}
                >
                  복사
                </Button>
              </span>
            ),
            order: (
              <span className={styles.two}>
                <span>{r.orderNumber ?? '—'}</span>
                <small>{r.orderOptionNumber ?? '—'}</small>
              </span>
            ),
            status: <Tag tone={status.tone}>{status.label}</Tag>,
            type: orderTypeLabel(r.orderType),
            cut: r.outboundCutSeqId ?? '—',
            collected: r.collectedDate ?? '—',
            goods: (
              <span className={styles.two}>
                <span className={styles.wrapText}>
                  {r.goodsName ?? '—'}
                  {r.goodsOption ? <span className={styles.dim}> · {r.goodsOption}</span> : null}
                </span>
                <small>{r.skuId ?? '—'}</small>
              </span>
            ),
            trace: (
              <Button variant="ghost" disabled={!traceKey || !onTrace || redispatchLocked} onClick={() => traceKey && onTrace?.(traceKey)}>
                Trace →
              </Button>
            ),
          },
        }
      }),
    [items, selected, redispatchLocked, onTrace],
  )

  return (
    <>
      <SectionHeading id="s-fopt-search" level={2} title="주문 조회" badge={<SourceBadge tone="new">신규 · GET /dev-console/orders</SourceBadge>} />

      <form className={styles.form} onSubmit={submit}>
        <Field label="출고 컷 차수 id">
          <input inputMode="numeric" value={form.outboundCutSeqId} placeholder="1840231" onChange={(e) => update('outboundCutSeqId', e.target.value)} />
        </Field>
        <Field label="주문번호">
          <input value={form.orderNumber} placeholder="202609151141320619_1" onChange={(e) => update('orderNumber', e.target.value)} />
        </Field>
        <Field label="원주문번호 (refNo1)">
          <input value={form.refNo1} placeholder="202609151141320619" onChange={(e) => update('refNo1', e.target.value)} />
        </Field>
        <Field label="주문 옵션번호 · 단독 조회 불가">
          <input value={form.orderOptionNumber} placeholder="487990013" onChange={(e) => update('orderOptionNumber', e.target.value)} />
        </Field>
        <div className={styles.group}>
          <span className={styles.groupLabel}>
            수집일 <span className={styles.dim}>· 최대 7일</span>
          </span>
          <div className={styles.dates}>
            <input type="date" aria-label="수집일 시작" value={form.collectedDateFrom} onChange={(e) => update('collectedDateFrom', e.target.value)} />
            <span>~</span>
            <input type="date" aria-label="수집일 끝" value={form.collectedDateTo} onChange={(e) => update('collectedDateTo', e.target.value)} />
          </div>
        </div>
        <Field label="한 번에">
          <select value={limit} onChange={(e) => changeLimit(Number(e.target.value))}>
            {ORDER_SEARCH_LIMITS.map((n) => (
              <option key={n} value={n}>
                {n}건
              </option>
            ))}
          </select>
        </Field>
        <div className={cx(styles.group, styles.wide)}>
          <span className={styles.groupLabel}>
            주문 상태 <span className={styles.dim}>· 여러 개 선택 · 수집일과 함께 쓰면 단독 조건이 됩니다</span>
          </span>
          <div className={styles.chips}>
            {ORDER_STATUS_GROUPS.map((group) => (
              <span key={group.label} style={{ display: 'contents' }}>
                <span className={styles.chipGroup}>{group.label}</span>
                {group.codes.map((code) => (
                  <button key={code} type="button" className={styles.chip} aria-pressed={form.orderStatuses.includes(code)} onClick={() => toggleStatus(code)}>
                    {orderStatusMeta(code).label}
                    <small>{code}</small>
                  </button>
                ))}
              </span>
            ))}
          </div>
        </div>

        <div className={cx(styles.submit, styles.wide)}>
          <p className={cx(styles.rule, rule.ok ? styles.ok : rule.severity === 'error' && styles.bad)} aria-live="polite">
            {rule.ok ? `조회 조건 — ${rule.basis}` : rule.message}
          </p>
          <Button variant="line" onClick={reset}>
            초기화
          </Button>
          <Button type="submit" disabled={!rule.ok || loading}>
            조회
          </Button>
        </div>
      </form>

      <div className={styles.results}>
        <ResultState state={panelState}>
          <section className={cx(styles.selbar, selected.length > 0 && styles.selbarOn)} aria-label="선택한 주문">
            <span className={styles.selCount}>{selected.length > 0 ? `${selected.length}건 선택` : '선택 없음'}</span>
            <span className={styles.selIds}>{selected.length > 0 ? selected.join(', ') : `행을 체크하면 재발사할 수 있어요 · 최대 ${MAX_REDISPATCH_IDS}건`}</span>
            {selected.length > 0 ? (
              <Button variant="ghost" onClick={() => setSelected([])}>
                선택 해제
              </Button>
            ) : null}
            <Button size="sm" disabled={selected.length === 0 || overLimit || redispatchLocked} onClick={() => onRedispatch([...selected])}>
              {`선택 ${selected.length}건 재발사`}
            </Button>
          </section>
          {overLimit ? <Callout tone="crit">한 번에 최대 {MAX_REDISPATCH_IDS}건까지 보낼 수 있어요. 선택을 줄여 주세요.</Callout> : null}

          <DataTable columns={columns} rows={rows} maxHeight={520} stickyHeader />

          {state.status === 'ready' && state.moreError ? <Callout tone="crit">{state.moreError}</Callout> : null}
          {state.status === 'ready' && state.hasNext ? (
            <div className={styles.more}>
              <span>{items.length}건 표시 · 더 있음</span>
              <Button variant="line" size="sm" disabled={state.loadingMore} onClick={loadMore}>
                {state.loadingMore ? '가져오는 중…' : '더 보기'}
              </Button>
            </div>
          ) : null}
        </ResultState>
      </div>
      <Note>전체 건수는 세지 않아요 — 컷 하나에 수만 건이 붙을 수 있어서요. 최신 주문부터 보여줘요. 조건을 바꾸면 처음부터 다시 불러요.</Note>
    </>
  )
}
